export function pdfToBookPage(pdfPage, pageOffset) {
  return pdfPage + pageOffset;
}

export function bookToPdfPage(bookPage, pageOffset) {
  return bookPage - pageOffset;
}

export function classificationToChunks(classification) {
  const chunks = [];
  const pageOffset = classification.page_offset !== undefined ? classification.page_offset : -1;
  
  function processChunk(chunk) {
    if (chunk.chunk_name) {
      const bookStartPage = pdfToBookPage(chunk.start_page || chunk.startPage || chunk.page, pageOffset);
      const bookEndPage = pdfToBookPage(chunk.end_page || chunk.endPage || chunk.page, pageOffset);
      chunks.push({
        id: chunk.chunk_name,
        name: chunk.name || chunk.title || '',
        startPage: bookStartPage,
        endPage: bookEndPage,
        type: chunk.type,
        originalChunkName: chunk.chunk_name,
        visibility: chunk.visibility || 'normal',
        language: chunk.language || null,
        detected_language: chunk.detected_language || null,
        copyright_holders: (chunk.copyright_holders || chunk.authors || []).map(holder => ({
          forename: holder.forename || holder.first_name || '',
          surname: holder.surname || holder.last_name || '',
          prefix: holder.prefix || null,
          unique_person_id: holder.unique_person_id || null,
          id_location: holder.id_location || null,
          orcid: holder.orcid || null,
        })),
      });
    }
    if (chunk.children?.length > 0) {
      chunk.children.forEach((child) => processChunk(child));
    }
  }
  
  const sourceData = classification.chunks || classification.Chunks || [];
  sourceData.forEach((c) => processChunk(c));
  
  chunks.sort((a, b) => a.startPage - b.startPage);
  
  if (chunks.length > 0 && chunks.some(c => !c.endPage)) {
    for (let i = 0; i < chunks.length - 1; i++) {
      if (!chunks[i].endPage) {
        chunks[i].endPage = chunks[i + 1].startPage - 1;
      }
    }

    if (!chunks[chunks.length - 1].endPage) {
      const totalPdfPages =
        typeof classification.document === 'object'
          ? classification.document.page_count
          : classification.document;

      const lastBookPage = pdfToBookPage(totalPdfPages, pageOffset);
      chunks[chunks.length - 1].endPage = lastBookPage;
    }
  }
  
  return chunks;
}

/**
 * Transform stored version chunk_data to local editor format.
 * This handles the format returned by GET /api/chunk-data/versions/{id}
 *
 * @param {Object} versionDetail - Data from the API endpoint
 *   Shape: { chunk_data: { chunks: [...], page_offset: number }, ... }
 * @returns {Object} - { chunks: [...], pageOffset: number }
 */
export function versionToChunks(versionDetail) {
  const { chunks: sourceChunks, page_offset } = versionDetail.chunk_data;
  const pageOffset = page_offset !== undefined ? page_offset : -1;

  const transformedChunks = [];

  function processChunk(chunk) {
    // Version chunks store pages as book pages (already converted)
    // Fields may be: startPage/start_page, endPage/end_page, name/title
    const startPage = chunk.startPage ?? chunk.start_page;
    const endPage = chunk.endPage ?? chunk.end_page;

    transformedChunks.push({
      id: chunk.chunk_name,
      name: chunk.name || chunk.title || '',
      startPage: startPage,
      endPage: endPage,
      type: chunk.type,
      originalChunkName: chunk.chunk_name,
      visibility: chunk.visibility || 'normal',
      language: chunk.language || null,
      detected_language: chunk.detected_language || null,
      copyright_holders: (chunk.copyright_holders || []).map((holder) => ({
        forename: holder.forename || holder.first_name || '',
        surname: holder.surname || holder.last_name || '',
        prefix: holder.prefix || null,
        unique_person_id: holder.unique_person_id || null,
        id_location: holder.id_location || null,
        orcid: holder.orcid || null,
      })),
    });

    // Process nested children if present
    if (chunk.children?.length > 0) {
      chunk.children.forEach((child) => processChunk(child));
    }
  }

  (sourceChunks || []).forEach((chunk) => processChunk(chunk));
  transformedChunks.sort((a, b) => a.startPage - b.startPage);

  return {
    chunks: transformedChunks,
    pageOffset,
  };
}

export function validateChunks(chunks) {
  if (chunks.length === 0) {
    return 'At least one chunk is required';
  }

  for (const c of chunks) {
    if (!c.name.trim()) {
      return 'All chunks must have names';
    }
    if (c.endPage < c.startPage) {
      return `Chunk "${c.name}": end page < start page`;
    }
  }

  return '';
}

export function getOverlapWarning(chunks) {
  if (chunks.length < 2) return '';
  const sorted = [...chunks].sort((a, b) => a.startPage - b.startPage);
  const overlappingPairs = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i].endPage >= sorted[i + 1].startPage) {
      overlappingPairs.push([sorted[i].name, sorted[i + 1].name]);
    }
  }
  if (overlappingPairs.length === 0) return '';
  const pairList = overlappingPairs.map(([a, b]) => `"${a}" and "${b}"`).join(', ');
  return `Overlapping chunks detected: ${pairList}`;
}
